/**
 * 查询字符串转换工具
 * 将 IFieldGroup[] 转换为可读的查询字符串
 */

import { drugDevStatusSearchFieldLangMap } from '@patsnap/synapse_common_config'
import { I18nLang, IDrugDevStatusSearchField } from '@patsnap/synapse_common_interface'
import type { IBAcTag } from '@pharmsnap/shared/types'
import { IFieldGroup, IFieldGroupFieldsKey } from '../../../../../../composable/useDrugFinderExpertSearch'
import { ALL_TAG_ID, getFieldDisplayName } from '../config/fieldConfig'
import { renderPropertyOptions } from '../config/propertyConfig'

/**
 * 将字段组转换为查询字符串 (优化版本)
 * 使用统一的 fieldConfig 配置，消除硬编码的字段名映射
 */
export function convertToQueryStringOptimized(groups: IFieldGroup[], locale: I18nLang = 'cn'): string {
  if (!groups || groups.length === 0) return ''

  const queryString = groups
    .map((group) => {
      // 处理每个group内部的fields
      const fieldQueries = Object.entries(group.fields)
        .filter(([, field]) => {
          // 过滤掉空值
          return (
            (field.tags && field.tags.length > 0) ||
            (field.selectedValues && field.selectedValues.length > 0 && field.selectedValues[0]?.id !== ALL_TAG_ID)
          )
        })
        .map(([key, field]) => {
          const fieldKey = key as IFieldGroupFieldsKey
          // notExpertFinderItems表示组合检索中没有的检索项目，使用drugDevStatusSearchFieldLangMap获取检索名称
          const fieldName =
            fieldKey === 'notExpertFinderItems'
              ? drugDevStatusSearchFieldLangMap[field.fields[0] as IDrugDevStatusSearchField]?.[locale]
              : getFieldDisplayName(fieldKey, locale)

          if (field.tags && field.tags.length > 0) {
            // 处理tags，用OR连接
            const tagValues = field.tags
              .map((tag: IBAcTag) => {
                const displayValue = locale === 'cn' ? tag.display_name_cn : tag.display_name_en

                // ATC字段特殊处理
                if (fieldKey === 'atcItems' && tag.atc_code) {
                  return `"${tag.atc_code || displayValue}"`
                }
                return `"${displayValue || tag.id}"`
              })
              .join(' OR ')
            return `${fieldName}: (${tagValues})`
          } else if (field.selectedValues && field.selectedValues.length > 0 && field.selectedValues[0]?.id !== ALL_TAG_ID) {
            // 处理selectedValues，用OR连接
            const values = field.selectedValues
              .map((val: IBAcTag) => {
                const displayValue = locale === 'cn' ? val.display_name_cn : val.display_name_en
                return `"${displayValue || val.id}"`
              })
              .join(' OR ')
            return `${fieldName}: (${values})`
          }
          return ''
        })
        .filter((query) => query !== '')
        .join(' AND ')

      return { query: fieldQueries, group }
    })
    .filter(({ query }) => query !== '')
    .map(({ query, group }, index) => {
      if (index === 0 && group.property !== 'milestone') return query

      // type === 'combined' 时，query 需要用括号包裹
      if (group.type === 'combined') {
        const propertyInfo = renderPropertyOptions.flatMap((item) => item.options).find((item) => item.value === group.property)
        const propertyName = locale === 'cn' ? propertyInfo?.display_name_cn : propertyInfo?.display_name_en

        // 里程碑日期字段需要单独处理
        if (group.property === 'milestone') {
          if (group.fields.milestoneDate?.selectedValues?.length) {
            const dateValue = group.fields.milestoneDate.selectedValues[0]
            const milestoneDateQuery = locale === 'cn' ? dateValue.display_name_cn : dateValue.display_name_en
            const milestoneItemsQuery = group.fields.milestoneItems?.selectedValues?.map((val: IBAcTag) => {
              const itemName = locale === 'cn' ? val.display_name_cn : val.display_name_en
              return `${itemName}: "${milestoneDateQuery}"`
            })
            return `${index === 0 ? '' : group.logic} ${propertyName}: (${milestoneItemsQuery?.join(' OR ')})`
          }
          return ''
        }

        // 适应症字段需要单独处理
        if (group.property === 'indication' && !group.fields.indicationItems?.tags?.length) {
          return ''
        }

        return `${group.logic} ${propertyName}: (${query})`
      }
      return `${group.logic} ${query}`
    })
    .join(' ')

  return queryString
}

/**
 * 导出兼容函数
 */
export { convertToQueryStringOptimized as convertToQueryString }
